import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Star, ShieldCheck, Eye, EyeOff, Trash2, Check, X, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= rating ? 'fill-honey text-honey-600' : 'text-ink/30'}`} />
      ))}
    </div>
  );
}

const TAB_META = {
  pending:  { label: 'Pending',  color: 'text-amber-600',  bg: 'bg-yellow-500/10 border-yellow-500/20' },
  approved: { label: 'Approved', color: 'text-green-600',   bg: 'bg-green-500/10 border-green-500/20' },
  hidden:   { label: 'Hidden',   color: 'text-red-600',     bg: 'bg-red-500/10 border-red-500/20' },
  all:      { label: 'All',      color: 'text-ink/70',    bg: 'bg-ink/[0.04] border-ink/10' },
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [tab, setTab] = useState('pending');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => { setSelected([]); setPage(1); }, [tab]);
  useEffect(() => { fetchReviews(); }, [tab, page]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reviews`, { params: { status: tab, page, limit: 20 }, withCredentials: true });
      setReviews(r.data.reviews);
      setTotal(r.data.total);
      setPendingCount(r.data.pending_count);
    } finally { setLoading(false); }
  };

  const updateReview = async (id, updates) => {
    await axios.put(`${API}/admin/reviews/${id}`, updates, { withCredentials: true });
    fetchReviews();
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true });
    fetchReviews();
  };

  const bulkAction = async (action) => {
    if (!selected.length) return;
    await axios.post(`${API}/admin/reviews/bulk-action`, { review_ids: selected, action }, { withCredentials: true });
    setSelected([]);
    fetchReviews();
  };

  const toggleAll = (e) => {
    setSelected(e.target.checked ? reviews.map(r => r.id) : []);
  };

  const toggleOne = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>Reviews</h1>
          <p className="text-ink/55 text-sm">{total} reviews in this view</p>
        </div>
        {pendingCount > 0 && (
          <span className="flex items-center gap-2 text-sm font-bold px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/20 text-amber-600 rounded-full">
            {pendingCount} pending approval
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {Object.entries(TAB_META).map(([key, meta]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${tab === key ? `${meta.bg} ${meta.color}` : 'bg-ink/[0.04] border-ink/10 text-ink/55 hover:text-ink'}`}>
            {meta.label} {key === 'pending' && pendingCount > 0 && <span className="ml-1 bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded-full">{pendingCount}</span>}
          </button>
        ))}
      </div>

      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-ink/[0.04] rounded-xl border border-ink/10 flex-wrap">
          <span className="text-honey-700 text-sm font-bold">{selected.length} selected</span>
          <div className="w-px h-4 bg-ink/[0.07]" />
          <button onClick={() => bulkAction('approve')} data-testid="bulk-approve-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 text-green-600 border border-green-500/20 rounded-lg text-sm font-bold hover:bg-green-500/20 transition-colors">
            <Check className="w-3 h-3" /> Approve All
          </button>
          <button onClick={() => bulkAction('hide')} data-testid="bulk-hide-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-600 border border-red-500/20 rounded-lg text-sm font-bold hover:bg-red-500/20 transition-colors">
            <EyeOff className="w-3 h-3" /> Hide All
          </button>
          <button onClick={() => setSelected([])} className="text-ink/45 hover:text-ink ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="admin-card rounded-xl overflow-hidden">
        <div className="border-b border-ink/10 px-4 py-3 grid grid-cols-12 gap-3 text-ink/55 text-xs uppercase tracking-widest items-center">
          <div className="col-span-1">
            <input type="checkbox" onChange={toggleAll} checked={selected.length === reviews.length && reviews.length > 0}
              className="rounded" style={{ accentColor: '#00E5FF' }} />
          </div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Rating</div>
          <div className="col-span-4">Review</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-honey border-t-transparent rounded-full animate-spin" /></div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <Star className="w-8 h-8 text-ink/15 mx-auto mb-2" />
            <p className="text-ink/45 text-sm">No reviews in this view</p>
          </div>
        ) : reviews.map(review => (
          <div key={review.id} className={`border-b border-ink/10 px-4 py-4 grid grid-cols-12 gap-3 items-start hover:bg-ink/[0.02] transition-colors ${review.hidden ? 'opacity-50' : ''}`}
            data-testid={`review-row-${review.id}`}>
            <div className="col-span-1 pt-0.5">
              <input type="checkbox" checked={selected.includes(review.id)} onChange={() => toggleOne(review.id)}
                className="rounded" style={{ accentColor: '#00E5FF' }} />
            </div>
            <div className="col-span-3">
              <p className="text-ink text-sm font-medium">{review.user_name}</p>
              <p className="text-ink/45 text-xs mt-0.5">{new Date(review.created_at).toLocaleDateString()}</p>
              {review.verified_purchase && (
                <span className="flex items-center gap-1 text-green-600 text-xs mt-1">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <div className="col-span-2">
              <Stars rating={review.rating} />
              <p className="text-ink/45 text-xs mt-1">{review.rating}/5</p>
            </div>
            <div className="col-span-4">
              {review.title && <p className="text-ink text-sm font-semibold mb-0.5">{review.title}</p>}
              <p className="text-ink/60 text-xs leading-relaxed line-clamp-3">{review.body || <span className="italic text-ink/30">No body</span>}</p>
              <p className="text-ink/30 text-xs mt-1">{review.helpful_count || 0} helpful votes</p>
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1.5">
              {/* Status badge */}
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
                review.hidden ? 'bg-red-500/10 text-red-600 border-red-500/20' :
                review.approved ? 'bg-green-500/10 text-green-600 border-green-500/20' :
                'bg-yellow-500/10 text-amber-600 border-yellow-500/20'
              }`}>
                {review.hidden ? 'Hidden' : review.approved ? 'Approved' : 'Pending'}
              </span>

              {/* Approve */}
              {!review.approved && !review.hidden && (
                <button onClick={() => updateReview(review.id, { approved: true })}
                  data-testid={`approve-btn-${review.id}`}
                  title="Approve"
                  className="p-1.5 bg-green-500/10 text-green-600 rounded-lg hover:bg-green-500/20 transition-colors">
                  <Check className="w-3.5 h-3.5" />
                </button>
              )}

              {/* Hide / Show */}
              {!review.hidden ? (
                <button onClick={() => updateReview(review.id, { hidden: true, approved: false })}
                  data-testid={`hide-btn-${review.id}`}
                  title="Hide"
                  className="p-1.5 bg-ink/[0.04] text-ink/55 rounded-lg hover:text-ink/85 hover:bg-ink/[0.07] transition-colors">
                  <EyeOff className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button onClick={() => updateReview(review.id, { hidden: false, approved: false })}
                  data-testid={`show-btn-${review.id}`}
                  title="Restore to Pending"
                  className="p-1.5 bg-ink/[0.04] text-ink/55 rounded-lg hover:text-ink/85 hover:bg-ink/[0.07] transition-colors">
                  <Eye className="w-3.5 h-3.5" />
                </button>
              )}

              {/* Delete */}
              <button onClick={() => deleteReview(review.id)}
                data-testid={`delete-review-btn-${review.id}`}
                title="Delete permanently"
                className="p-1.5 bg-red-500/10 text-red-600 rounded-lg hover:bg-red-500/20 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {total > 20 && (
        <div className="flex justify-center gap-2 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-4 py-2 bg-ink/[0.04] text-ink/70 rounded-xl text-sm disabled:opacity-30">Prev</button>
          <span className="px-4 py-2 text-ink/55 text-sm">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={reviews.length < 20}
            className="px-4 py-2 bg-ink/[0.04] text-ink/70 rounded-xl text-sm disabled:opacity-30">Next</button>
        </div>
      )}
    </AdminLayout>
  );
}
